import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import FileUploaderMultiMedia, {
  DEFAULT_MAX_FILE_SIZE,
  VIDEO_MAX_FILE_SIZE,
} from "../../FileUpload/FileUploaderMultiMedia";
import { validateInputs } from "../../../utils/validation";
import { toast } from "react-toastify";

const PURPLE = "#7028DA";

const IMAGE_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const VIDEO_ACCEPT = {
  "video/mp4": [".mp4"],
};

const CATEGORY_OPTIONS = "Artificial";
const PRODUCT_TYPE_OPTIONS = [
  "Ring",
  "Necklace",
  "Earrings",
  "Bangle",
  "Mangalsutra",
  "Pendant",
  "Chain",
  "Nose pin",
  "Anklet",
  "Earing chain",
  "Other",
];
const GENDER_OPTIONS = ["Women", "Men", "Kids", "Unisex"];
const OCCASION_OPTIONS = [
  "Wedding",
  "Party",
  "Office",
  "Festive",
  "Daily",
  "Other",
];
const WARRANTY_OPTIONS = [
  { value: "0", label: "No warranty" },
  { value: "3", label: "3 Months" },
  { value: "6", label: "6 Months" },
  { value: "12", label: "12 Months (1 Year)" },
  { value: "18", label: "18 Months" },
  { value: "24", label: "24 Months (2 Years)" },
  { value: "36", label: "36 Months (3 Years)" },
];
const RETURN_OPTIONS = [
  { value: "0", label: "No returns / exchange" },
  { value: "3", label: "3 Days" },
  { value: "7", label: "7 Days" },
  { value: "10", label: "10 Days" },
  { value: "15", label: "15 Days" },
  { value: "30", label: "30 Days" },
];
const ELIGIBILITY_OPTIONS = [
  "Unused / unworn",
  "Original packaging intact",
  "Tags and certificate intact",
  "Not customised or engraved",
  "Manufacturing defect only",
];
// keeps an old saved value selectable even if it isn't in the new list
const withCurrent = (options, value, unit) =>
  value && !options.some((o) => o.value === value)
    ? [...options, { value, label: `${value} ${unit}` }]
    : options;

const initialForm = {
  productCode: "",
  name: "",
  title: "",
  subtitle: "",
  description: "",
  category: CATEGORY_OPTIONS,
  productType: "",
  productTypeOther: "",
  subType: "",
  collection: "",
  gender: "",
  designStyle: "",
  occasion: [],
  occasionOther: "",
  tags: [],
  careInstructions: "",
  warranty: "",
  returnExchange: "",
  eligibility: "",
  mrp: "",
  discount: "",
  finalAmount: "",
  includeGst: false,
  gstPercentage: "",
  files: [],
};

const calcFinalAmount = (mrp, discount, includeGst, gstPercentage) => {
  const m = parseFloat(mrp);
  if (Number.isNaN(m)) return "";
  const d = Math.min(Math.max(parseFloat(discount) || 0, 0), 100);
  const afterDiscount = m - (m * d) / 100;
  const parsedGst = Number(gstPercentage);
  const gst =
    String(gstPercentage).trim() === "" || !Number.isFinite(parsedGst)
      ? null
      : Math.min(Math.max(parsedGst, 0), 100);
  const finalAmount =
    includeGst && gst !== null
      ? afterDiscount + (afterDiscount * gst) / 100
      : afterDiscount;
  return finalAmount.toFixed(2);
};

const isVideoFile = (file) => {
  const fileUrl =
    typeof file === "string"
      ? file
      : file?.url || file?.path || file?.name || "";
  return Boolean(
    /\.(mp4|webm|mov|m4v|ogg|avi|mkv)(\?.*)?$/i.test(fileUrl) ||
    file?.type?.includes("video") ||
    file?.mediaType?.includes("video"),
  );
};

const getFileValue = (file) => {
  if (typeof file === "string" || file instanceof File) return file;
  return (
    file?.url ||
    file?.videoUrl ||
    file?.video ||
    file?.path ||
    file?.location ||
    ""
  );
};

const AddEditJewellery = ({
  open,
  onClose,
  onSubmit,
  editData = null,
  loading = false,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [errorMessages, setErrorMessages] = useState({});

  useEffect(() => {
    if (!open) {
      setFormData(initialForm);
      setErrorMessages({});
      return;
    }

    if (editData) {
      const includeGst = Boolean(editData.includeGst ?? editData.gstIncluded);

      const savedProductType = editData.productType ?? ""; // NEW
      const savedOccasions = Array.isArray(editData.occasion)
        ? editData.occasion
        : editData.occasion
          ? String(editData.occasion)
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      const customOccasions = savedOccasions.filter(
        (o) => !OCCASION_OPTIONS.includes(o),
      );
      setFormData({
        ...initialForm,
        productCode: editData.productCode ?? "",
        name: editData.name ?? "",
        title: editData.title ?? "",
        subtitle: editData.subtitle ?? "",
        description: editData.description ?? "",
        category: editData.category ?? CATEGORY_OPTIONS,
        productType:
          savedProductType && !PRODUCT_TYPE_OPTIONS.includes(savedProductType)
            ? "Other"
            : savedProductType,
        productTypeOther:
          savedProductType && !PRODUCT_TYPE_OPTIONS.includes(savedProductType)
            ? savedProductType
            : "",
        subType: editData.subType ?? "",
        collection: editData.collection ?? "",
        gender: editData.gender ?? "",
        designStyle: editData.designStyle ?? "",
        occasion: customOccasions.length
          ? [
              ...new Set([
                ...savedOccasions.filter((o) => OCCASION_OPTIONS.includes(o)),
                "Other",
              ]),
            ]
          : savedOccasions, // CHANGED
        occasionOther: customOccasions.join(", "), // NEW
        tags: Array.isArray(editData.tags)
          ? editData.tags
          : editData.tags
            ? String(editData.tags)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        careInstructions: editData.careInstructions ?? "",
        warranty:
          editData.warrantyPeriod != null
            ? String(editData.warrantyPeriod)
            : "",
        returnExchange:
          editData.returnExchangePeriod != null
            ? String(editData.returnExchangePeriod)
            : "",
        eligibility: editData.returnExchangeEligibility ?? "",
        mrp: editData.mrp ?? "",
        discount: editData.discount ?? "",
        finalAmount: editData.finalAmount ?? "",
        includeGst,
        gstPercentage: includeGst ? (editData.gstPercentage ?? "") : "",
        files: Array.isArray(editData.files)
          ? editData.files.map(getFileValue).filter(Boolean)
          : [],
      });
      setErrorMessages({});
    }
  }, [open, editData]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    if (name === "includeGst" && !checked) {
      setFormData((previous) => {
        const next = {
          ...previous,
          includeGst: false,
          gstPercentage: "",
        };
        next.finalAmount = calcFinalAmount(
          next.mrp,
          next.discount,
          next.includeGst,
          next.gstPercentage,
        );
        return next;
      });
      setErrorMessages((previous) => ({ ...previous, gstPercentage: "" }));
      return;
    }
    setFormData((previous) => {
      const next = {
        ...previous,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "includeGst" || name === "gstPercentage") {
        next.finalAmount = calcFinalAmount(
          next.mrp,
          next.discount,
          next.includeGst,
          next.gstPercentage,
        );
      }
      return next;
    });
    if (name === "gstPercentage") {
      setErrorMessages((previous) => ({ ...previous, gstPercentage: "" }));
    }
  };

  const handlePriceChange = (event) => {
    const { name, value } = event.target;
    if (!/^\d*\.?\d{0,2}$/.test(value)) return;
    if (name === "discount" && value !== "" && Number(value) > 100) return;

    setFormData((previous) => {
      const next = { ...previous, [name]: value };
      next.finalAmount = calcFinalAmount(
        next.mrp,
        next.discount,
        next.includeGst,
        next.gstPercentage,
      );
      return next;
    });
    setErrorMessages((prev) => ({ ...prev, [name]: "", finalAmount: "" }));
  };

  const handleMediaChange = (newFiles, mediaType) => {
    const isValidFile = (file) => {
      if (!(file instanceof File)) return true;
      if (mediaType === "image") {
        return (
          (["image/jpeg", "image/png"].includes(file.type) ||
            /\.(jpe?g|png)$/i.test(file.name)) &&
          file.size <= DEFAULT_MAX_FILE_SIZE
        );
      }
      return (
        (file.type === "video/mp4" || /\.mp4$/i.test(file.name)) &&
        file.size <= VIDEO_MAX_FILE_SIZE
      );
    };
    const validFiles = newFiles.filter(isValidFile);

    if (validFiles.length !== newFiles.length) {
      toast.error(
        mediaType === "image"
          ? "Only JPG/PNG images up to 500 KB are allowed"
          : "Only MP4 videos up to 5 MB are allowed",
      );
    }

    setFormData((previous) => ({
      ...previous,
      files: [
        ...previous.files.filter(
          (file) => isVideoFile(file) !== (mediaType === "video"),
        ),
        ...validFiles,
      ],
    }));
  };

  const validations = [
    {
      tagid: "name",
      value: formData.name,
      regex_name: "free_text",
      min: 1,
      max: 200,
      errmsg: "Enter a name",
      required: true,
    },
    {
      tagid: "productType",
      value: formData.productType,
      regex_name: "free_text",
      min: 1,
      max: 200,
      errmsg: "Select a product type",
      required: true,
    },
    {
      tagid: "mrp",
      value: formData.mrp,
      regex_name: "min_number",
      min: 0,
      errmsg: "Enter a valid MRP",
      required: true,
    },
    ...(formData.productType === "Other"
      ? [
          {
            tagid: "productTypeOther",
            value: formData.productTypeOther,
            regex_name: "free_text",
            min: 1,
            max: 200,
            errmsg: "Enter a product type",
            required: true,
          },
        ]
      : []),
    ...([].concat(formData.occasion).includes("Other")
      ? [
          {
            tagid: "occasionOther",
            value: formData.occasionOther,
            regex_name: "free_text",
            min: 1,
            max: 200,
            errmsg: "Enter an occasion",
            required: true,
          },
        ]
      : []),
    ...(formData.includeGst
      ? [
          {
            tagid: "gstPercentage",
            value: formData.gstPercentage,
            regex_name: "min_max_number",
            min: 0,
            max: 100,
            errmsg: "Enter a GST percentage from 0 to 100",
            required: true,
          },
        ]
      : []),
  ];

  const handleSubmit = () => {
    setErrorMessages({});
    if (formData.includeGst && String(formData.gstPercentage).trim() === "") {
      setErrorMessages({ gstPercentage: "GST percentage is required" });
      return;
    }

    if (!formData.files.some((file) => !isVideoFile(file))) {
      setErrorMessages({ files: "At least one image is required" });
      return;
    }

    const result = validateInputs(validations, {
      error_display_html: true,
      highlight_all_errors: true,
    });

    if (result === true) {
      const data = new FormData();

      const payloadFields = {
        productCode: formData.productCode.trim(),
        name: formData.name,
        title: formData.title,
        subtitle: formData.subtitle,
        description: formData.description,
        category: formData.category,
        productType:
          formData.productType === "Other"
            ? formData.productTypeOther.trim()
            : formData.productType,
        subType: formData.subType,
        collection: formData.collection,
        gender: formData.gender,
        designStyle: formData.designStyle,
        occasion: []
          .concat(formData.occasion)
          .map((o) => (o === "Other" ? formData.occasionOther.trim() : o))
          .join(","),
        tags: formData.tags.join(","),
        careInstructions: formData.careInstructions,
        warrantyPeriod: formData.warranty,
        returnExchangePeriod: formData.returnExchange,
        returnExchangeEligibility: formData.eligibility,
        mrp: formData.mrp,
        discount: formData.discount || 0,
        finalAmount: formData.finalAmount,
        gstIncluded: formData.includeGst,
        ...(formData.includeGst
          ? { gstPercentage: Number(formData.gstPercentage) }
          : {}),
      };

      for (const [key, val] of Object.entries(payloadFields)) {
        if (val !== undefined && val !== null) {
          data.append(key, String(val));
        }
      }

      formData.files.forEach((file) => data.append("files", file));

      onSubmit(data);
    } else {
      const errorObj = result.reduce((acc, curr) => {
        acc[curr.tagid] = curr.errmsg;
        return acc;
      }, {});
      setErrorMessages(errorObj);
    }
  };

  const images = formData.files.filter((file) => !isVideoFile(file));
  const videos = formData.files.filter(isVideoFile);

  const requiredStar = <span style={{ color: "red" }}>*</span>;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)",
          color: "#fff",
          py: 2.5,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {editData ? "Edit Jewellery" : "Add Jewellery"}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Enter Jewellery details, pricing, and media
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Product Code"
              placeholder="e.g. XYZ-123"
              name="productCode"
              value={formData.productCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={<>Name {requiredStar}</>}
              placeholder="Enter name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errorMessages.name}
              helperText={errorMessages.name}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={<>Title </>}
              placeholder="Enter title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              error={!!errorMessages.title}
              helperText={errorMessages.title}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Subtitle"
              placeholder="Enter subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              error={!!errorMessages.subtitle}
              helperText={errorMessages.subtitle}
            />
          </Grid>

          <Grid item size={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label="Description"
              placeholder="Enter description"
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Category"
              value={CATEGORY_OPTIONS}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-root": { background: "#f5f5fa" } }}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={<>Product Type {requiredStar}</>}
              name="productType"
              value={formData.productType}
              onChange={handleChange}
              error={!!errorMessages.productType}
              helperText={errorMessages.productType}
            >
              {PRODUCT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            {formData.productType === "Other" && (
              <TextField
                fullWidth
                size="small"
                label={<>Custom Product Type </>}
                placeholder="Enter product type"
                name="productTypeOther"
                value={formData.productTypeOther}
                onChange={handleChange}
                error={!!errorMessages.productTypeOther}
                helperText={errorMessages.productTypeOther}
                sx={{ mt: 2 }}
              />
            )}
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Sub-type"
              placeholder="Enter sub-type"
              name="subType"
              value={formData.subType}
              onChange={handleChange}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={<>Collection </>}
              placeholder="Enter collection"
              name="collection"
              value={formData.collection}
              onChange={handleChange}
              error={!!errorMessages.collection}
              helperText={errorMessages.collection}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={<>Gender </>}
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              error={!!errorMessages.gender}
              helperText={errorMessages.gender}
            >
              {GENDER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={<>Design Style </>}
              placeholder="Enter design style"
              name="designStyle"
              value={formData.designStyle}
              onChange={handleChange}
              error={!!errorMessages.designStyle}
              helperText={errorMessages.designStyle}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={OCCASION_OPTIONS}
              value={formData.occasion}
              onChange={(_, v) => {
                setFormData((p) => ({ ...p, occasion: v }));
                setErrorMessages((p) => ({ ...p, occasion: "" }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={<>Occasion </>}
                  error={!!errorMessages.occasion}
                  helperText={errorMessages.occasion}
                />
              )}
            />
            {[].concat(formData.occasion).includes("Other") && ( // NEW
              <TextField
                fullWidth
                size="small"
                label={<>Custom Occasion </>}
                placeholder="Enter occasion"
                name="occasionOther"
                value={formData.occasionOther}
                onChange={handleChange}
                error={!!errorMessages.occasionOther}
                helperText={errorMessages.occasionOther}
                sx={{ mt: 2 }}
              />
            )}
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              multiple
              freeSolo
              autoSelect
              options={[]}
              value={formData.tags}
              onChange={(_, v) =>
                setFormData((p) => ({
                  ...p,
                  tags: [
                    ...new Set(
                      v.map((t) => t.trim().replace(/,/g, "")).filter(Boolean),
                    ),
                  ],
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Tags"
                  placeholder="Type and press Enter"
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Care Instructions"
              placeholder="Enter care instructions"
              name="careInstructions"
              value={formData.careInstructions}
              onChange={handleChange}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={<>Warranty </>}
              name="warranty"
              value={formData.warranty}
              onChange={handleChange}
              error={!!errorMessages.warranty}
              helperText={errorMessages.warranty}
            >
              {withCurrent(WARRANTY_OPTIONS, formData.warranty, "Months").map(
                (o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={<>Return and Exchange </>}
              placeholder="Enter period"
              name="returnExchange"
              value={formData.returnExchange}
              onChange={(e) => {
                handleChange(e);
                if (e.target.value === "0") {
                  setFormData((p) => ({ ...p, eligibility: "No returns" }));
                } else {
                  setFormData((p) =>
                    p.eligibility === "No returns"
                      ? { ...p, eligibility: "" }
                      : p,
                  );
                }
              }}
              error={!!errorMessages.returnExchange}
              helperText={errorMessages.returnExchange}
            >
              {withCurrent(RETURN_OPTIONS, formData.returnExchange, "Days").map(
                (o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={<>Eligibility </>}
              placeholder="Enter eligibility"
              name="eligibility"
              value={formData.eligibility}
              onChange={handleChange}
              disabled={formData.returnExchange === "0"}
              error={!!errorMessages.eligibility}
              helperText={errorMessages.eligibility}
            >
              {[
                ...new Set([
                  ...(formData.eligibility ? [formData.eligibility] : []),
                  ...(formData.returnExchange === "0"
                    ? ["No returns"]
                    : ELIGIBILITY_OPTIONS),
                ]),
              ].map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* pricing */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label={<>MRP {requiredStar}</>}
              placeholder="Enter MRP"
              name="mrp"
              value={formData.mrp}
              onChange={handlePriceChange}
              error={!!errorMessages.mrp}
              helperText={errorMessages.mrp}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Discount %"
              placeholder="Enter discount %"
              name="discount"
              value={formData.discount}
              onChange={handlePriceChange}
              error={!!errorMessages.discount}
              helperText={errorMessages.discount}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Final Amount"
              placeholder="Enter final amount"
              name="finalAmount"
              value={formData.finalAmount}
              error={!!errorMessages.finalAmount}
              helperText={
                errorMessages.finalAmount ||
                (formData.includeGst
                  ? "Auto-calculated: MRP − discount % + GST %"
                  : "Auto-calculated: MRP − discount %")
              }
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
              sx={{ "& .MuiInputBase-root": { background: "#f5f5fa" } }}
            />
          </Grid>
          <Grid
            item
            size={{ xs: 12, sm: 6 }}
            sx={{
              display: "flex",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: "100%",
              }}
            >
              <FormControlLabel
                sx={{ m: 0, flexShrink: 0 }}
                control={
                  <Checkbox
                    name="includeGst"
                    checked={formData.includeGst}
                    onChange={handleChange}
                    sx={{
                      color: "#B7BAC6",
                      "&.Mui-checked": { color: PURPLE },
                    }}
                  />
                }
                label="Include GST"
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label={<>GST Percentage (%) </>}
                placeholder="Enter GST percentage"
                name="gstPercentage"
                value={formData.gstPercentage}
                onChange={handleChange}
                inputProps={{ min: 0, max: 100, step: "any" }}
                error={!!errorMessages.gstPercentage}
                helperText={errorMessages.gstPercentage}
                sx={{
                  flex: { xs: "1 0 100%", sm: "1 1 0%" },
                  minWidth: 0,
                  visibility: formData.includeGst ? "visible" : "hidden",
                }}
              />
            </Box>
          </Grid>

          {/* media */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#606778", fontWeight: 600, mb: 1 }}
            >
              Images {requiredStar}
            </Typography>
            <FileUploaderMultiMedia
              value={images}
              onChange={(files) => handleMediaChange(files, "image")}
              accept={IMAGE_ACCEPT}
              maxFiles={5}
              maxFileSize={DEFAULT_MAX_FILE_SIZE}
              mediaType="image"
              title="Add Jewellery Images"
              helperText="JPG, PNG only — max 500 KB each — up to 5 images"
            />
            {errorMessages.files && (
              <Typography variant="caption" color="error">
                {errorMessages.files}
              </Typography>
            )}
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#606778", fontWeight: 600, mb: 1 }}
            >
              Videos
            </Typography>
            <FileUploaderMultiMedia
              value={videos}
              onChange={(files) => handleMediaChange(files, "video")}
              accept={VIDEO_ACCEPT}
              maxFiles={3}
              maxFileSize={VIDEO_MAX_FILE_SIZE}
              mediaType="video"
              title="Add Jewellery Videos"
              helperText="MP4 only — max 5 MB each — up to 3 videos"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #e8eaf0",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: "#752ADA", px: 3, "&:hover": { bgcolor: "#5f1fb8" } }}
        >
          {editData ? "Update Jewellery" : "Save Jewellery"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditJewellery;
